import { FC, useMemo, memo } from 'react'
import { InputSelection } from 'components/features/socialFeed/ComposeInput/ComposeInput'
import { parseText } from 'helpers/composeHelpers'
import { ComposeId } from 'interfaces/feed'
import styles from 'components/features/socialFeed/ComposeDecorations/ComposeDecorations.styl'
import { ComposeDecoration } from 'components/features/socialFeed/ComposeDecoration/ComposeDecoration'
import { useComposeState } from 'helpers/hookHelpers'

export type ComposeDecorationsProps = {
  composeId: ComposeId
  selection: InputSelection
  text: string
}

export const ComposeDecorations: FC<ComposeDecorationsProps> = memo((props) => {
  const {
    selection,
    text,
    composeId,
  } = props
  const { selectionStart, selectionEnd } = selection

  const { type } = useComposeState(props.composeId, { type: null })

  const decorations = useMemo(() => {
    if (text === null || type === null) {
      return []
    }

    return parseText(text, type)
  }, [text, type])

  const selectedIndex = useMemo(() => {
    if (!decorations) {
      return -1
    }

    return decorations.findIndex((decoration) => (
      selectionStart === selectionEnd &&
      selectionEnd > decoration.start &&
      selectionEnd <= decoration.end
    ))
  }, [selectionStart, selectionEnd, decorations])

  const decorationsChildren = useMemo(() => {
    if (text === null || type === null || decorations === null) {
      return null
    }

    let prevIndex = 0
    return decorations.reduce<(JSX.Element | string)[]>((acc, decoration, index) => {
      const { start, end } = decoration
      if (prevIndex !== start) {
        acc.push(text.substring(prevIndex, start))
      }

      if (start !== end) {
        acc.push(
          <div key={ index } className={ styles.decoration }>
            <ComposeDecoration
              composeId={ composeId }
              decoration={ decoration }
              isSelected={ selectedIndex === index }
              text={ text }
            />
          </div>
        )
      }

      const isLast = index === decorations.length - 1
      if (isLast) {
        acc.push(text.substr(end))
      } else {
        prevIndex = end
      }

      return acc
    }, [])
  }, [text, type, decorations, composeId, selectedIndex])

  if (!decorationsChildren) {
    return null
  }

  // `contentEditable` used to setup textarea like word wrap rules only
  return (
    <div
      className={ styles.composeDecorations }
      contentEditable
      suppressContentEditableWarning
    >
      { decorationsChildren }
    </div>
  )
})
